import { FC, ReactNode } from "react";
import { WebClipV1SessionPart, SessionEventKind } from "../session.types";
import styled from "@emotion/styled";
import { MDSSessionPartContainer } from "@/mds/components/sessions/session-parts/session-part.style";
import { SessionPartContext } from "@/mds/components/sessions/session-parts/session-part.types";
import { MDSBubble } from "@/mds/components/bubble/bubble";
import { MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble/bubble.types";
import { sessionPartContainsImage } from "@/mds/components/sessions/session.utils";
import {
  WEB_CLIP_MAX_HEIGHT_SIZE_PX,
  WEB_CLIP_MAX_WIDTH_SIZE_PX,
} from "@/mds/components/sessions/session.constants";

interface Props {
  part: WebClipV1SessionPart;
  context: SessionPartContext;
}

export const MDSSessionWebClipV1Part: FC<Props> = ({ part, context }) => {
  const partContainsImage = sessionPartContainsImage(part);

  const WrapperComponent: FC<IWrapperComponentProps> = (() => {
    if (context.event_kind === SessionEventKind.AgentActionV1) {
      return AgentActionWrapper;
    }

    if (partContainsImage) {
      return ImageWrapper;
    }

    return StandardWrapper;
  })();

  return (
    <WrapperComponent isLastInGroup={context.isLastInGroup}>
      <Container event_kind={context.event_kind}>
        {part.clipped_image_uploaded_file_url ? (
          <WebClipImage
            src={part.clipped_image_uploaded_file_url}
            alt={part.captured_website_title ?? "Clipped Web Page"}
            hasClippedTextContent={!!part.clipped_text_content}
          />
        ) : null}
        {part.clipped_text_content ? (
          <ClippedTextContent>{part.clipped_text_content}</ClippedTextContent>
        ) : null}
      </Container>
    </WrapperComponent>
  );
};

interface IWrapperComponentProps {
  children: ReactNode;
  isLastInGroup?: boolean;
}

const AgentActionWrapper: FC<IWrapperComponentProps> = ({ children }) => {
  return <>{children}</>;
};

const ImageWrapper: FC<IWrapperComponentProps> = ({ children, isLastInGroup }) => {
  return (
    <Bubble
      variant={MDSBubbleVariant.Image}
      align={MDSBubbleAlignment.Right}
      isLastInGroup={isLastInGroup}
    >
      {children}
    </Bubble>
  );
};

const StandardWrapper: FC<IWrapperComponentProps> = ({ children, isLastInGroup }) => {
  return (
    <Bubble
      variant={MDSBubbleVariant.Grey}
      align={MDSBubbleAlignment.Right}
      isLastInGroup={isLastInGroup}
    >
      {children}
    </Bubble>
  );
};

const Bubble = styled(MDSBubble)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.xxs};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
  margin-right: 0;
`;

const Container = styled(MDSSessionPartContainer)<{
  event_kind: SessionEventKind;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: ${WEB_CLIP_MAX_WIDTH_SIZE_PX}px;
  max-height: ${WEB_CLIP_MAX_HEIGHT_SIZE_PX}px;
  align-items: center;
  margin-top: ${({ theme, event_kind }) =>
    event_kind === SessionEventKind.AgentActionV1 ? theme.spacing.xs : "0"};
  margin-bottom: ${({ theme, event_kind }) =>
    event_kind === SessionEventKind.AgentActionV1 ? theme.spacing.xs : "0"};
`;

const WebClipImage = styled.img<{ hasClippedTextContent: boolean }>`
  aspect-ratio: 3 / 2;
  object-fit: cover;
  object-position: top;
  border-radius: ${({ hasClippedTextContent }) =>
    hasClippedTextContent ? "14px 14px 0 0" : "14px"};
  border-bottom: none;
  height: 100%;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.grey.x100};
`;

const ClippedTextContent = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.grey.x600};
  padding: 0px 12px 16px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  width: 100%;
`;
